import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PaymentTimingInfoIcon,
  SamplingTooltipArrowIcon as TooltipArrow
} from '@/components/common/icon';

const VIEWPORT_MARGIN = 24;
const TOOLTIP_GAP = 4;
const ARROW_WIDTH = 16;
const ARROW_HEIGHT = 6;

/** Shared by the video card and all modal/drawer entry points. */
export default function PaymentTimingVideoNote() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    arrowLeft: number;
    placement: 'top' | 'bottom';
    maxBodyHeight?: number;
  }>({ left: 24, top: 24, arrowLeft: 172, placement: 'top' });
  const tooltipId = useId();
  const label = t('payment_timing.video_note_label', {
    defaultValue: 'About payment terms in this video'
  });
  const note = t('payment_timing.video_note', {
    defaultValue:
      'Payment terms have been updated since this video was recorded. Please check the latest announcement and your collaboration details for the payment terms that apply to your order.'
  });

  useLayoutEffect(() => {
    if (!open) return;
    let disposed = false;
    const updatePosition = () => {
      if (!buttonRef.current || !tooltipRef.current || !contentRef.current)
        return;
      const trigger = buttonRef.current.getBoundingClientRect();
      const tip = tooltipRef.current.getBoundingClientRect();
      const naturalBodyHeight = Math.max(
        contentRef.current.scrollHeight,
        contentRef.current.getBoundingClientRect().height,
        tip.height - ARROW_HEIGHT
      );
      const naturalHeight = naturalBodyHeight + ARROW_HEIGHT;
      const above = Math.max(0, trigger.top - TOOLTIP_GAP - VIEWPORT_MARGIN);
      const below = Math.max(
        0,
        window.innerHeight - VIEWPORT_MARGIN - trigger.bottom - TOOLTIP_GAP
      );
      const placement =
        naturalHeight <= above
          ? 'top'
          : naturalHeight <= below
            ? 'bottom'
            : above >= below
              ? 'top'
              : 'bottom';
      const maxBodyHeight = Math.max(
        0,
        (placement === 'top' ? above : below) - ARROW_HEIGHT
      );
      const height = Math.min(naturalBodyHeight, maxBodyHeight) + ARROW_HEIGHT;
      const triggerCenter = trigger.left + trigger.width / 2;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          triggerCenter - tip.width / 2,
          window.innerWidth - VIEWPORT_MARGIN - tip.width
        )
      );
      const preferredTop =
        placement === 'top'
          ? trigger.top - TOOLTIP_GAP - height
          : trigger.bottom + TOOLTIP_GAP;
      const top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(preferredTop, window.innerHeight - VIEWPORT_MARGIN - height)
      );
      setPosition({
        left,
        top,
        placement,
        maxBodyHeight,
        arrowLeft: Math.max(
          0,
          Math.min(
            triggerCenter - left - ARROW_WIDTH / 2,
            tip.width - ARROW_WIDTH
          )
        )
      });
    };
    updatePosition();
    // Loading the local Inter face can change wrapping after the first layout.
    document.fonts?.ready.then(() => {
      if (!disposed) updatePosition();
    });
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      disposed = true;
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, note]);

  return (
    <span
      className="relative inline-flex shrink-0 align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(document.activeElement))
          setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        className="inline-flex h-4 w-4 items-center justify-center border-0 bg-transparent p-0"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
          }
        }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <PaymentTimingInfoIcon
          className={open ? 'block text-[#737373]' : 'block text-[#A3A3A3]'}
        />
      </button>
      {open
        ? createPortal(
            <span
              ref={tooltipRef}
              role="tooltip"
              id={tooltipId}
              data-payment-timing-tooltip=""
              data-placement={position.placement}
              className="fixed z-[10000] block w-[360px] max-w-[calc(100vw-48px)] text-left font-['Inter'] text-xs font-semibold leading-[18px] text-white"
              style={{
                left: position.left,
                top: position.top,
                paddingTop: position.placement === 'bottom' ? ARROW_HEIGHT : 0,
                paddingBottom: position.placement === 'top' ? ARROW_HEIGHT : 0
              }}
            >
              <span
                ref={contentRef}
                data-payment-timing-content=""
                data-testid="payment-timing-content"
                className="block max-h-[calc(100vh-54px)] overflow-y-auto rounded-lg bg-[#0A0A0A] px-3 py-2"
                style={{
                  maxHeight: position.maxBodyHeight,
                  boxShadow:
                    '0 2px 2px -1px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.03), 0 12px 16px -4px rgba(0, 0, 0, 0.08)'
                }}
                key={note}
              >
                {note}
              </span>
              <span
                aria-hidden="true"
                data-payment-timing-arrow=""
                data-testid="payment-timing-arrow"
                className="pointer-events-none absolute h-[6px] w-4"
                style={{
                  left: position.arrowLeft,
                  top: position.placement === 'bottom' ? 0 : undefined,
                  bottom: position.placement === 'top' ? 0 : undefined
                }}
              >
                <TooltipArrow
                  aria-hidden="true"
                  className="absolute"
                  style={{
                    bottom: position.placement === 'top' ? 0 : undefined,
                    top: position.placement === 'bottom' ? 0 : undefined,
                    transform:
                      position.placement === 'bottom'
                        ? 'rotate(180deg)'
                        : undefined
                  }}
                />
              </span>
            </span>,
            document.body
          )
        : null}
    </span>
  );
}
